import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Lock, Paperclip, Send, Loader2 } from "lucide-react";

// Shared threaded ticket conversation. `base` is the API prefix for this viewer:
//   staff:  /clients/{wid}/tickets/{taskId}   portal: /portal/tickets/{taskId}
export function TicketThread({ base, taskId, staff = false, uploadUrl, onChanged }) {
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState("");
  const [internal, setInternal] = useState(false);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const load = () => api.get(`${base}/comments`).then((r) => setComments(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [base]);

  const attach = async (e) => {
    const f = e.target.files?.[0]; if (!f || !uploadUrl) return;
    const fd = new FormData(); fd.append("file", f); fd.append(staff ? "related_type" : "task_id", staff ? "task" : taskId); if (staff) { fd.append("related_id", taskId); }
    try { const { data } = await api.post(uploadUrl, fd, { headers: { "Content-Type": "multipart/form-data" } }); setFiles((x) => [...x, { file_id: data.id, name: data.name }]); }
    catch (err) { toast.error(apiError(err)); }
    e.target.value = "";
  };

  const send = async () => {
    if (!body.trim() && files.length === 0) return;
    setBusy(true);
    try { await api.post(`${base}/comments`, { body, internal: staff && internal, attachments: files }); setBody(""); setFiles([]); setInternal(false); await load(); onChanged?.(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="ticket-thread">
      <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
        {comments.length === 0 && <p className="text-sm text-slate-400">No replies yet.</p>}
        {comments.map((c) => (
          <div key={c.id} className={`rounded-xl px-3 py-2 text-sm border ${c.internal ? "bg-amber-50 border-amber-200" : c.author_kind === "client" ? "bg-sky-50 border-sky-100" : "bg-white border-slate-200"}`} data-testid="ticket-comment">
            <div className="flex items-center gap-2 text-[11px] text-slate-500 mb-1"><span className="font-medium text-slate-700">{c.author_name}</span><span>· {c.author_kind}</span>{c.internal && <span className="flex items-center gap-1 text-amber-700"><Lock className="w-3 h-3" /> internal only</span>}<span className="ml-auto">{new Date(c.created_at).toLocaleString()}</span></div>
            <p className="whitespace-pre-wrap text-slate-800">{c.body}</p>
            {c.attachments?.length > 0 && <div className="flex flex-wrap gap-1 mt-1">{c.attachments.map((a) => <span key={a.file_id} className="chip border-slate-200 text-slate-600 text-[11px]"><Paperclip className="w-3 h-3 mr-1 inline" />{a.name}</span>)}</div>}
          </div>
        ))}
      </div>
      <div className="border rounded-xl p-2 bg-white" style={{ borderColor: "var(--line)" }}>
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder={staff ? "Reply to the client… (toggle internal to keep it private)" : "Write a reply…"} className="min-h-[70px] border-0 focus-visible:ring-0 text-sm" data-testid="ticket-reply-input" />
        {files.length > 0 && <div className="flex flex-wrap gap-1 px-1">{files.map((f) => <span key={f.file_id} className="chip border-slate-200 text-[11px]">{f.name}</span>)}</div>}
        <div className="flex items-center gap-2 pt-2 px-1">
          {uploadUrl && <><input ref={fileRef} type="file" className="hidden" onChange={attach} data-testid="ticket-attach-input" /><Button variant="ghost" size="sm" onClick={() => fileRef.current?.click()} data-testid="ticket-attach-btn"><Paperclip className="w-4 h-4" /></Button></>}
          {staff && <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer"><input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} data-testid="ticket-internal-toggle" /><Lock className="w-3 h-3" /> Internal only</label>}
          <Button size="sm" className="ml-auto rounded-full" onClick={send} disabled={busy} data-testid="ticket-reply-send">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Send</Button>
        </div>
      </div>
    </div>
  );
}
